import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuthStore as useAuthStoreRoot } from './store/authStore'
import { AppLayout } from './components/layout/AppLayout'
import { ProtectedRoute } from './components/auth/ProtectedRoute'
import { OnboardingGuard } from './components/auth/OnboardingGuard'
import { ModuleGate } from './components/ui/ModuleGate'
import { ReportGate } from './components/ui/ReportGate'
import { FullPageSpinner } from './components/ui/Spinner'
import { ErrorBoundary } from './components/ErrorBoundary'
import { useAuthInit } from './hooks/useAuth'
import { AdminLayout } from './admin/components/AdminLayout'
import { AdminProtectedRoute } from './admin/components/AdminProtectedRoute'
import { supabase } from './lib/supabase'
import MaintenancePage from './pages/MaintenancePage'
import { detectHost, isCustomHostCandidate } from './lib/host/detectHost'

// Palstro Commerce storefront base domain — strip from window.location.hostname
// to detect storefront subdomains (kelani.palstro.com). Point at 'localhost' to
// exercise the subdomain branch in local dev.
const STOREFRONT_BASE_DOMAIN = import.meta.env.VITE_STOREFRONT_BASE_DOMAIN || 'palstro.com'

// ─── Admin pages (lazy) ───────────────────────────────────────────────────────
const AdminLoginPage         = lazy(() => import('./admin/pages/AdminLoginPage'))
const OverviewPage           = lazy(() => import('./admin/pages/OverviewPage'))
const TenantsPage            = lazy(() => import('./admin/pages/TenantsPage'))
const TenantDetailPage       = lazy(() => import('./admin/pages/TenantDetailPage'))
const SubscriptionsAdminPage = lazy(() => import('./admin/pages/SubscriptionsAdminPage'))
const RevenuePage            = lazy(() => import('./admin/pages/RevenuePage'))
const PlatformSettingsPage   = lazy(() => import('./admin/pages/PlatformSettingsPage'))
const AnnouncementsPage      = lazy(() => import('./admin/pages/AnnouncementsPage'))
const PlatformAuditPage      = lazy(() => import('./admin/pages/PlatformAuditPage'))
const PlatformExpensesPage   = lazy(() => import('./admin/pages/PlatformExpensesPage'))
const FeatureFlagsPage       = lazy(() => import('./admin/pages/FeatureFlagsPage'))
const CustomDomainsAdminPage = lazy(() => import('./admin/pages/CustomDomainsAdminPage'))
const ShowcaseAdminPage      = lazy(() => import('./admin/pages/ShowcasePage'))

// ─── Auth pages (eager) ───────────────────────────────────────────────────────
import LoginPage from './pages/auth/LoginPage'
import RegisterPage from './pages/auth/RegisterPage'
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage'
import ForcePasswordChangePage from './pages/auth/ForcePasswordChangePage'
import MarketingHomePage from './pages/MarketingHomePage'

// ─── App pages (lazy) ────────────────────────────────────────────────────────
const DashboardPage     = lazy(() => import('./pages/dashboard/DashboardPage'))
const POSEntryPage      = lazy(() => import('./pages/pos/POSEntryPage'))
const POSOpenPage       = lazy(() => import('./pages/pos/POSOpenPage'))
const POSPage           = lazy(() => import('./pages/pos/POSPage'))
const NewSalePage       = lazy(() => import('./pages/sales/NewSalePage'))
const SaleDetailPage    = lazy(() => import('./pages/sales/SaleDetailPage'))
const InventoryPage     = lazy(() => import('./pages/inventory/InventoryPage'))
const StockPage         = lazy(() => import('./pages/stock/StockPage'))
const LocationsPage     = lazy(() => import('./pages/locations/LocationsPage'))
const NewPurchasePage     = lazy(() => import('./pages/purchases/NewPurchasePage'))
const NewPurchasePagePOS  = lazy(() => import('./pages/purchases/NewPurchasePagePOS'))
const PurchaseDetailPage  = lazy(() => import('./pages/purchases/PurchaseDetailPage'))
const ExpensesPage      = lazy(() => import('./pages/expenses/ExpensesPage'))
const ExpensesReportPage = lazy(() => import('./pages/expenses/ExpensesReportPage'))
const ContactsPage           = lazy(() => import('./pages/contacts/ContactsPage'))
const SupplierLedgerPage     = lazy(() => import('./pages/contacts/SupplierLedgerPage'))
const CustomerLedgerPage     = lazy(() => import('./pages/contacts/CustomerLedgerPage'))
const PayrollLayout            = lazy(() => import('./pages/payroll/PayrollLayout'))
const PayrollSettingsHubPage   = lazy(() => import('./pages/payroll/settings/PayrollSettingsHubPage'))
const PayrollStaffPage         = lazy(() => import('./pages/payroll/StaffPage'))
const StaffDetailPage          = lazy(() => import('./pages/payroll/StaffDetailPage'))
const RunPayrollPage           = lazy(() => import('./pages/payroll/RunPayrollPage'))
const PayslipsPage             = lazy(() => import('./pages/payroll/PayslipsPage'))
const SalaryPaymentsPage       = lazy(() => import('./pages/payroll/SalaryPaymentsPage'))
const PayrollTaxSettingsPage   = lazy(() => import('./pages/payroll/settings/TaxSettingsPage'))
const SalaryGradesPage         = lazy(() => import('./pages/payroll/settings/SalaryGradesPage'))
const WorkingSchedulesPage     = lazy(() => import('./pages/payroll/WorkingSchedulesPage'))
const BulkSetupPage            = lazy(() => import('./pages/payroll/settings/BulkSetupPage'))
const AccountingPage          = lazy(() => import('./pages/accounting/AccountingPage'))
const JournalPage             = lazy(() => import('./pages/accounting/JournalPage'))
const ChartOfAccountsPage     = lazy(() => import('./pages/accounting/ChartOfAccountsPage'))
const TrialBalancePage        = lazy(() => import('./pages/accounting/TrialBalancePage'))
const ManualJournalPage       = lazy(() => import('./pages/accounting/ManualJournalPage'))
const LedgerPage              = lazy(() => import('./pages/accounting/LedgerPage'))
const OpeningBalancesPage     = lazy(() => import('./pages/accounting/OpeningBalancesPage'))
const SubsidiaryLedgerPage    = lazy(() => import('./pages/accounting/SubsidiaryLedgerPage'))
const BackfillPage            = lazy(() => import('./pages/accounting/BackfillPage'))
const AssetsPage              = lazy(() => import('./pages/accounting/AssetsPage'))
const TaxRemittancePage       = lazy(() => import('./pages/accounting/TaxRemittancePage'))
const BankReconcilePage       = lazy(() => import('./pages/accounting/BankReconcilePage'))
const LoansPage               = lazy(() => import('./pages/accounting/LoansPage'))
const SchedulesPage           = lazy(() => import('./pages/accounting/SchedulesPage'))
const FinancialYearsPage      = lazy(() => import('./pages/accounting/FinancialYearsPage'))
const BudgetSetupPage         = lazy(() => import('./pages/accounting/BudgetSetupPage'))
const BudgetVsActualPage      = lazy(() => import('./pages/reports/BudgetVsActualPage'))
const BadDebtReportPage       = lazy(() => import('./pages/reports/BadDebtReportPage'))
const FinancialStatementsPage = lazy(() => import('./pages/reports/FinancialStatementsPage'))
const SalesAnalysisPage       = lazy(() => import('./pages/reports/SalesAnalysisPage'))
const InventoryValuationPage  = lazy(() => import('./pages/reports/InventoryValuationPage'))
const OperationsReportPage    = lazy(() => import('./pages/reports/OperationsReportPage'))
const ProductSalesReportPage  = lazy(() => import('./pages/reports/ProductSalesReportPage'))
const SalesPaymentReportPage  = lazy(() => import('./pages/reports/SalesPaymentReportPage'))
const PurchasePaymentReportPage = lazy(() => import('./pages/reports/PurchasePaymentReportPage'))
const ProductPurchaseReportPage = lazy(() => import('./pages/reports/ProductPurchaseReportPage'))
const ProductMovementReportPage = lazy(() => import('./pages/reports/ProductMovementReportPage'))
const NavPreviewPage          = lazy(() => import('./pages/nav-preview/NavPreviewPage'))
const SellLayout              = lazy(() => import('./pages/sell/SellLayout'))
const SellIndexRedirect       = lazy(() => import('./pages/sell/SellLayout').then(m => ({ default: m.SellIndexRedirect })))
const SellInvoicesTab         = lazy(() => import('./pages/sell/tabs/SellInvoicesTab'))
const OnlineOrdersTab         = lazy(() => import('./pages/sell/tabs/OnlineOrdersTab'))
const OnlineOrderDetailPage   = lazy(() => import('./pages/online-orders/OnlineOrderDetailPage'))
const POSSessionsTab          = lazy(() => import('./pages/sell/tabs/POSSessionsTab'))
const SellReturnsTab          = lazy(() => import('./pages/sell/tabs/SellReturnsTab'))
const SalesReportPage         = lazy(() => import('./pages/sell/SalesReportPage'))
const BuyLayout               = lazy(() => import('./pages/buy/BuyLayout'))
const BuyIndexRedirect        = lazy(() => import('./pages/buy/BuyLayout').then(m => ({ default: m.BuyIndexRedirect })))
const BuyPurchasesTab         = lazy(() => import('./pages/buy/tabs/BuyPurchasesTab'))
const BuyInTransitTab         = lazy(() => import('./pages/buy/tabs/BuyInTransitTab'))
const BuyReturnsTab           = lazy(() => import('./pages/buy/tabs/BuyReturnsTab'))
const AccountingHubPage       = lazy(() => import('./pages/accounting/AccountingHubPage'))
const ReportingHubPage        = lazy(() => import('./pages/reports/ReportingHubPage'))
const NewSalesReturnPage      = lazy(() => import('./pages/sales/NewSalesReturnPage'))
const SaleReturnDetailPage    = lazy(() => import('./pages/sales/SaleReturnDetailPage'))
const LowStockPage            = lazy(() => import('./pages/inventory/LowStockPage'))
const StockTakePage           = lazy(() => import('./pages/inventory/StockTakePage'))
const StockTransfersPage      = lazy(() => import('./pages/inventory/StockTransfersPage'))
const NewTransferPage         = lazy(() => import('./pages/inventory/NewTransferPage'))
const TransferDetailPage      = lazy(() => import('./pages/inventory/TransferDetailPage'))
const InterbankTransferPage   = lazy(() => import('./pages/accounting/InterbankTransferPage'))
const OwnerDrawingsPage       = lazy(() => import('./pages/accounting/OwnerDrawingsPage'))
const FinancialRatiosPage     = lazy(() => import('./pages/accounting/FinancialRatiosPage'))
const CreditLimitsPage        = lazy(() => import('./pages/customers/CreditLimitsPage'))
const InvoiceNumberingPage    = lazy(() => import('./pages/settings/InvoiceNumberingPage'))
const RecurringExpensesPage   = lazy(() => import('./pages/settings/RecurringExpensesPage'))
const CurrenciesPage          = lazy(() => import('./pages/settings/CurrenciesPage'))
const AgedDebtorsPage         = lazy(() => import('./pages/reports/AgedDebtorsPage'))
const AgedCreditorsPage       = lazy(() => import('./pages/reports/AgedCreditorsPage'))
const ReportsPage       = lazy(() => import('./pages/reports/ReportsPage'))
const BudgetPage        = lazy(() => import('./pages/budget/BudgetPage'))
const NotificationsPage       = lazy(() => import('./pages/notifications/NotificationsPage'))
const PalstroMessagesPage     = lazy(() => import('./pages/palstro-messages/PalstroMessagesPage'))
const AuditPage         = lazy(() => import('./pages/audit/AuditPage'))
const BarcodePage       = lazy(() => import('./pages/barcode/BarcodePage'))
const POSEndOfDayPage   = lazy(() => import('./pages/pos/EndOfDayPage'))
const SubscriptionPage  = lazy(() => import('./pages/subscription/SubscriptionPage'))
const OnlineStoreDetailsPage = lazy(() => import('./pages/online-store/OnlineStoreDetailsPage'))
const OnlineProductsPage     = lazy(() => import('./pages/online-store/OnlineProductsPage'))
const NotFoundPage      = lazy(() => import('./pages/NotFoundPage'))
const OnboardingPage    = lazy(() => import('./pages/onboarding/OnboardingPage'))
const OrphanAccountPage = lazy(() => import('./pages/OrphanAccountPage'))

// ─── Settings sub-pages (lazy) ───────────────────────────────────────────────
const SettingsLayout           = lazy(() => import('./pages/settings/SettingsLayout'))
const BusinessSettingsPage     = lazy(() => import('./pages/settings/BusinessSettingsPage'))
const ProfilePage              = lazy(() => import('./pages/settings/ProfilePage'))
const UsersPage                = lazy(() => import('./pages/settings/UsersPage'))
const RolesPage                = lazy(() => import('./pages/settings/RolesPage'))
const CreateRolePage           = lazy(() => import('./pages/settings/CreateRolePage'))
const RoleEditPage             = lazy(() => import('./pages/settings/RoleEditPage'))
const ModulePermissionsPage    = lazy(() => import('./pages/settings/ModulePermissionsPage'))
const TenantModulesPage        = lazy(() => import('./pages/settings/TenantModulesPage'))
const CustomFieldsPage         = lazy(() => import('./pages/settings/CustomFieldsPage'))
const ExpenseCategoriesPage    = lazy(() => import('./pages/settings/ExpenseCategoriesPage'))
const PrinterSettingsPage      = lazy(() => import('./pages/settings/PrinterSettingsPage'))
const AuditSettingsPage        = lazy(() => import('./pages/audit/AuditPage'))

// ─── Business Settings (Phase 1 reorg) ───────────────────────────────────────
const BusinessSettingsLayout   = lazy(() => import('./pages/business-settings/BusinessSettingsLayout'))
const CommerceSettingsPage     = lazy(() => import('./pages/business-settings/CommerceSettingsPage'))
const BusinessProfilePage      = lazy(() => import('./pages/business-settings/BusinessProfilePage'))
const FinanceSettingsLayout    = lazy(() => import('./pages/business-settings/FinanceSettingsLayout'))
const BusinessTaxSettingsPage  = lazy(() => import('./pages/business-settings/finance/BusinessTaxSettingsPage'))

// ─── Finance & System (Phase 1.7 sidebar reorg) ──────────────────────────────
const FinanceLayout                 = lazy(() => import('./pages/finance/FinanceLayout'))
const FixedAssetsLayout             = lazy(() => import('./pages/finance/fixed-assets/FixedAssetsLayout'))
const FixedAssetRegisterPage        = lazy(() => import('./pages/finance/fixed-assets/FixedAssetRegisterPage'))
const FixedAssetCategoriesPage      = lazy(() => import('./pages/finance/fixed-assets/FixedAssetCategoriesPage'))
const FixedAssetDetailPage          = lazy(() => import('./pages/finance/fixed-assets/FixedAssetDetailPage'))
const DepreciationRunsPage          = lazy(() => import('./pages/finance/fixed-assets/DepreciationRunsPage'))
const DisposalsPage                 = lazy(() => import('./pages/finance/fixed-assets/DisposalsPage'))
const TaxComplianceLayout           = lazy(() => import('./pages/finance/tax-compliance/TaxComplianceLayout'))
const CitAccrualPage                = lazy(() => import('./pages/finance/tax-compliance/CitAccrualPage'))
const VatReturnsPage                = lazy(() => import('./pages/finance/tax-compliance/VatReturnsPage'))
const PayrollTaxReturnsPage         = lazy(() => import('./pages/finance/tax-compliance/PayrollTaxReturnsPage'))
const WhtReturnsPage                = lazy(() => import('./pages/finance/tax-compliance/WhtReturnsPage'))
const PaymentsReportLayout          = lazy(() => import('./pages/trading/payments/PaymentsReportLayout'))
const CustomerPaymentsTab           = lazy(() => import('./pages/trading/payments/CustomerPaymentsTab'))
const VendorPaymentsTab             = lazy(() => import('./pages/trading/payments/VendorPaymentsTab'))
const ExpensePaymentsTab            = lazy(() => import('./pages/trading/payments/ExpensePaymentsTab'))
const SalesPerRepPage          = lazy(() => import('./pages/trading/sales-rep/SalesPerRepPage'))
const IncentivesPage           = lazy(() => import('./pages/trading/incentives/IncentivesPage'))
const IncentivePayablesPage    = lazy(() => import('./pages/trading/incentive-payables/PayablesPage'))
const TradingLayout            = lazy(() => import('./pages/trading/TradingLayout'))
const SystemLayout             = lazy(() => import('./pages/system/SystemLayout'))

// ─── Phase 3 — page-level Roles & Permissions ───────────────────────────────
const RolesListPage            = lazy(() => import('./pages/business-settings/RolesListPage'))
const RoleDetailPage           = lazy(() => import('./pages/business-settings/RoleDetailPage'))
import { PermissionGuard } from './components/permissions/PermissionGuard'
import type { PermissionLevel } from './lib/permissions/pageRegistry'

// Thin alias for keeping the routing block readable.
function G({ k, l = 'view', children }: { k: string; l?: PermissionLevel; children: React.ReactNode }) {
  return <PermissionGuard pageKey={k} requiredLevel={l}>{children}</PermissionGuard>
}

// ─── Storefront / Palstro Commerce (lazy, public) ─────────────────────────────
const StorefrontApp = lazy(() => import('./pages/storefront/StorefrontApp'))
const CustomHostStorefront = lazy(() => import('./pages/storefront/CustomHostStorefront'))

// ─── Support (lazy) ──────────────────────────────────────────────────────────
const SupportPage         = lazy(() => import('./pages/support/SupportPage'))
const HelpPage            = lazy(() => import('./pages/help/HelpPage'))

// ─── Parent portal (lazy, public) ────────────────────────────────────────────
const ParentLoginPage     = lazy(() => import('./pages/parent/ParentLoginPage'))
const ParentDashboard     = lazy(() => import('./pages/parent/ParentDashboard'))

// ─── Logistics module (lazy) ─────────────────────────────────────────────────
const LogisticsDashboardPage   = lazy(() => import('./pages/logistics/LogisticsDashboardPage'))
const ShipmentsPage            = lazy(() => import('./pages/logistics/ShipmentsPage'))
const NewShipmentPage          = lazy(() => import('./pages/logistics/NewShipmentPage'))
const ShipmentDetailPage       = lazy(() => import('./pages/logistics/ShipmentDetailPage'))
const FleetPage                = lazy(() => import('./pages/logistics/FleetPage'))
const HubsRoutesPage           = lazy(() => import('./pages/logistics/HubsRoutesPage'))
const LiveTrackingPage         = lazy(() => import('./pages/logistics/LiveTrackingPage'))
const LogisticsSettingsPage    = lazy(() => import('./pages/settings/LogisticsSettingsPage'))
const FinancialStatementSettingsPage = lazy(() => import('./pages/settings/FinancialStatementSettingsPage'))
const PeriodLocksPage          = lazy(() => import('./pages/settings/PeriodLocksPage'))
const DriverAppPage            = lazy(() => import('./pages/driver/DriverAppPage'))
const PublicTrackingPage       = lazy(() => import('./pages/tracking/PublicTrackingPage'))
const PublicInvoicePage        = lazy(() => import('./pages/invoice/PublicInvoicePage'))
// Logistics Phase 2
const TariffPage               = lazy(() => import('./pages/logistics/TariffPage'))
const LogisticsCustomersPage   = lazy(() => import('./pages/logistics/LogisticsCustomersPage'))
const ManifestsPage            = lazy(() => import('./pages/logistics/ManifestsPage'))
const ManifestDetailPage       = lazy(() => import('./pages/logistics/ManifestDetailPage'))
const InvoicesPage             = lazy(() => import('./pages/logistics/InvoicesPage'))
const SettlementsPage          = lazy(() => import('./pages/logistics/SettlementsPage'))

// ─── School module (lazy) ────────────────────────────────────────────────────
const SchoolLayout           = lazy(() => import('./pages/school/SchoolLayout'))
const SchoolDashboard        = lazy(() => import('./pages/school/SchoolDashboard'))
const StudentsPage           = lazy(() => import('./pages/school/StudentsPage'))
const ClassesPage            = lazy(() => import('./pages/school/ClassesPage'))
const SubjectsPage           = lazy(() => import('./pages/school/SubjectsPage'))
const SchoolStaffPage        = lazy(() => import('./pages/school/SchoolStaffPage'))
const FeeManagementPage      = lazy(() => import('./pages/school/FeeManagementPage'))
const SchoolReportsPage      = lazy(() => import('./pages/school/SchoolReportsPage'))
const ParentPortalPage       = lazy(() => import('./pages/school/ParentPortalPage'))
const PromotionPage          = lazy(() => import('./pages/school/PromotionPage'))
const IDCardsPage            = lazy(() => import('./pages/school/IDCardsPage'))
// School — new pages
const AlumniPage             = lazy(() => import('./pages/school/AlumniPage'))
const WaitlistPage           = lazy(() => import('./pages/school/WaitlistPage'))
const DiscountReportPage     = lazy(() => import('./pages/school/DiscountReportPage'))
const AttendancePage         = lazy(() => import('./pages/school/AttendancePage'))
const OutstandingFeesPage    = lazy(() => import('./pages/school/OutstandingFeesPage'))
const CalendarPage           = lazy(() => import('./pages/school/CalendarPage'))
const FeeStructurePage       = lazy(() => import('./pages/school/FeeStructurePage'))
const StudentPaymentsPage    = lazy(() => import('./pages/school/StudentPaymentsPage'))
const PendingApprovalsPage   = lazy(() => import('./pages/school/PendingApprovalsPage'))
const BankAccountsPage       = lazy(() => import('./pages/school/BankAccountsPage'))
const ScoreEntryPage         = lazy(() => import('./pages/school/ScoreEntryPage'))
const ReportCardsPage        = lazy(() => import('./pages/school/ReportCardsPage'))
const ExamSettingsPage       = lazy(() => import('./pages/school/ExamSettingsPage'))
const StaffTypesPage         = lazy(() => import('./pages/school/StaffTypesPage'))
const FeeComponentsPage      = lazy(() => import('./pages/school/FeeComponentsPage'))
const TimetablePage          = lazy(() => import('./pages/school/TimetablePage'))
const BehaviourPage          = lazy(() => import('./pages/school/BehaviourPage'))
const TransportPage          = lazy(() => import('./pages/school/TransportPage'))
const HostelsPage            = lazy(() => import('./pages/school/HostelsPage'))

// ─── Maintenance Mode check (cached 60s, skips /admin/*) ────────────────────
type MaintenanceStatus = { active: boolean; message: string; endTime: string }
const MAINT_CACHE_KEY = 'palstro-maint'
const MAINT_TTL = 60_000

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string): any => (supabase as any).from(t)

function useMaintenance() {
  const [status, setStatus] = useState<MaintenanceStatus>({ active: false, message: '', endTime: '' })
  const fetching = useRef(false)

  useEffect(() => {
    if (fetching.current) return
    const cached = (() => {
      try {
        const r = JSON.parse(sessionStorage.getItem(MAINT_CACHE_KEY) ?? '{}')
        return Date.now() - (r.ts ?? 0) < MAINT_TTL ? r : null
      } catch { return null }
    })()
    if (cached) { setStatus(cached); return }
    fetching.current = true
    db('platform_settings')
      .select('key,value')
      .in('key', ['maintenance_mode', 'maintenance_message', 'maintenance_end_time'])
      .then(({ data }: { data: Array<{ key: string; value: string }> | null }) => {
        const map: Record<string, string> = {}
        ;(data ?? []).forEach(r => { map[r.key] = r.value })
        const result: MaintenanceStatus = {
          active: map['maintenance_mode'] === 'true',
          message: map['maintenance_message'] ?? '',
          endTime: map['maintenance_end_time'] ?? '',
        }
        sessionStorage.setItem(MAINT_CACHE_KEY, JSON.stringify({ ...result, ts: Date.now() }))
        setStatus(result)
        fetching.current = false
      })
      .catch(() => { fetching.current = false })
  }, [])

  return status
}

function RootRedirect() {
  const { isAuthenticated, isInitializing } = useAuthStoreRoot()
  if (isInitializing) return <FullPageSpinner />
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <MarketingHomePage />
}

export default function App() {
  const location = useLocation()

  // Detect the storefront host up front (pure fn of host + path) so we can decide,
  // BEFORE the hooks, whether this is the public customer surface. A subdomain or
  // custom-domain storefront is anon and uses its own isolated customerClient, so we
  // skip staff auth-init there entirely — that keeps its subscription / online-store
  // poll (and the error flood it caused on flaky networks) off the shopper's shop.
  const host = detectHost(window.location.hostname, location.pathname, STOREFRONT_BASE_DOMAIN)
  const isSubdomainStore = host.mode === 'storefront' && host.source === 'subdomain'
  const isCustomHostStore = isCustomHostCandidate(window.location.hostname, STOREFRONT_BASE_DOMAIN)
  const isStorefrontHost = isSubdomainStore || isCustomHostStore

  useAuthInit(!isStorefrontHost)
  const maint = useMaintenance()

  // Storefront host branch — runs BEFORE the maintenance check so a live shop on
  // a subdomain does not go dark during a PLATFORM maintenance window. Path-based
  // storefronts (palstro.com/shop/:slug) are handled by a route below, not here.
  if (isSubdomainStore) {
    return (
      <Suspense fallback={<FullPageSpinner />}>
        <StorefrontApp slug={host.slug!} basePath="" />
      </Suspense>
    )
  }

  // Phase 0.3 — custom-domain storefront (e.g. larry-retails.com). Any host that is
  // NOT the platform's own space and NOT a dev/preview host is asked (server-side) to
  // resolve to a live shop; if it does, we mount the SAME slug-keyed StorefrontApp as
  // the subdomain path. This runs BEFORE the maintenance check for the same reason the
  // subdomain branch does — a live custom-domain shop must not go dark on platform
  // maintenance.
  if (isCustomHostStore) {
    return (
      <Suspense fallback={<FullPageSpinner />}>
        <CustomHostStorefront host={window.location.host} />
      </Suspense>
    )
  }

  // Show maintenance page for all routes except /admin/*
  if (maint.active && !location.pathname.startsWith('/admin')) {
    return <MaintenancePage message={maint.message} endTime={maint.endTime} />
  }

  return (
    <ErrorBoundary>
      <Suspense fallback={<FullPageSpinner />}>
        <Routes>
          {/* Public */}
          <Route path="/login"           element={<LoginPage />} />
          <Route path="/register"        element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/"                element={<RootRedirect />} />

          {/* Force password change — sits OUTSIDE ProtectedRoute so it's reachable
              while the user is still flagged. The page itself requires a session
              (supabase.auth.updateUser will fail otherwise). */}
          <Route path="/auth/change-password" element={<ForcePasswordChangePage />} />

          {/* Onboarding wizard — auth required but BYPASSES OnboardingGuard */}
          <Route element={<ProtectedRoute />}>
            <Route path="/onboarding" element={<OnboardingPage />} />
            {/* Orphan fallback — authenticated user with no active workspace.
                Full-page, no AppLayout / OnboardingGuard. */}
            <Route path="/orphan" element={<OrphanAccountPage />} />
          </Route>

          {/* POS — full-screen, no AppLayout */}
          <Route element={<ProtectedRoute />}>
            <Route element={<OnboardingGuard />}>
              <Route path="/pos"                          element={<G k="sell.pos"><POSEntryPage /></G>} />
              <Route path="/pos/open"                     element={<G k="sell.pos" l="edit"><POSOpenPage /></G>} />
              <Route path="/pos/session/:id"              element={<G k="sell.pos" l="edit"><POSPage /></G>} />
              <Route path="/pos/sessions/:id/report"      element={<G k="sell.pos"><POSEndOfDayPage /></G>} />
            </Route>
          </Route>

          {/* Fully protected — requires auth + tenant + onboarding */}
          <Route element={<ProtectedRoute />}>
            <Route element={<OnboardingGuard />}>
            <Route element={<AppLayout />}>
              <Route path="/dashboard"     element={<G k="dashboard"><DashboardPage /></G>} />
              {/* Trading — parent layout with mini sub-sidebar wrapping hub pages */}
              <Route path="/trading" element={<Navigate to="/buy" replace />} />
              <Route element={<TradingLayout />}>
                <Route path="/buy" element={<BuyLayout />}>
                  <Route index element={<BuyIndexRedirect />} />
                  <Route path="purchases"  element={<G k="buy.purchases"><BuyPurchasesTab /></G>} />
                  <Route path="in-transit" element={<G k="buy.in_transit"><BuyInTransitTab /></G>} />
                  <Route path="returns"    element={<G k="buy.returns"><BuyReturnsTab /></G>} />
                </Route>
                <Route path="/sell" element={<SellLayout />}>
                  <Route index element={<SellIndexRedirect />} />
                  <Route path="invoices"     element={<G k="sell.invoices"><SellInvoicesTab /></G>} />
                  <Route path="pos"          element={<G k="sell.pos" l="edit"><POSEntryPage /></G>} />
                  <Route path="pos-sessions" element={<G k="sell.pos_sessions"><POSSessionsTab /></G>} />
                  <Route path="returns"      element={<G k="sell.returns"><SellReturnsTab /></G>} />
                  <Route path="online-orders"     element={<G k="sell.online_orders"><OnlineOrdersTab /></G>} />
                  <Route path="online-orders/:id" element={<G k="sell.online_orders"><OnlineOrderDetailPage /></G>} />
                  <Route path="report"       element={<G k="sell.invoices"><SalesReportPage /></G>} />
                </Route>
                <Route path="/payments" element={<PaymentsReportLayout />}>
                  <Route index element={<Navigate to="/payments/customers" replace />} />
                  <Route path="customers" element={<G k="trading.payments.customers"><CustomerPaymentsTab /></G>} />
                  <Route path="vendors"   element={<G k="trading.payments.vendors"><VendorPaymentsTab /></G>} />
                  <Route path="expenses"  element={<G k="trading.payments.expenses"><ExpensePaymentsTab /></G>} />
                  <Route path="salary"    element={<Navigate to="/payroll/salary-payments" replace />} />
                </Route>
                <Route path="/sales-per-rep" element={<G k="trading.sales_per_rep"><SalesPerRepPage /></G>} />
                <Route path="/incentives" element={<G k="trading.incentives"><IncentivesPage /></G>} />
                <Route path="/incentive-payables" element={<G k="trading.incentive_payables"><IncentivePayablesPage /></G>} />
                <Route path="/inventory/*"   element={<G k="inventory.products"><InventoryPage /></G>} />
                <Route path="/expenses/report" element={<G k="expenses.list"><ExpensesReportPage /></G>} />
                <Route path="/expenses/*"    element={<G k="expenses.list"><ExpensesPage /></G>} />
                <Route path="/contacts/suppliers/:supplierId/ledger" element={<G k="contacts.list"><SupplierLedgerPage /></G>} />
                <Route path="/contacts/customers/:customerId/ledger" element={<G k="contacts.list"><CustomerLedgerPage /></G>} />
                <Route path="/contacts/*"    element={<G k="contacts.list"><ContactsPage /></G>} />
              </Route>
              {/* Finance — parent layout with nested sub-nav */}
              <Route path="/finance" element={<FinanceLayout />}>
                <Route index element={<Navigate to="/finance/accounting" replace />} />
                <Route path="accounting" element={<G k="accounting.hub"><AccountingHubPage /></G>} />
                <Route path="reporting"  element={<G k="reporting.hub"><ReportingHubPage /></G>} />
                <Route path="fixed-assets" element={<FixedAssetsLayout />}>
                  <Route index element={<Navigate to="/finance/fixed-assets/register" replace />} />
                  <Route path="register"     element={<G k="finance.fixed_assets.register"><FixedAssetRegisterPage /></G>} />
                  <Route path="categories"   element={<G k="finance.fixed_assets.categories"><FixedAssetCategoriesPage /></G>} />
                  <Route path="depreciation" element={<G k="finance.fixed_assets.depreciation"><DepreciationRunsPage /></G>} />
                  <Route path="disposals"    element={<G k="finance.fixed_assets.disposals"><DisposalsPage /></G>} />
                  <Route path=":assetId"     element={<G k="finance.fixed_assets.detail"><FixedAssetDetailPage /></G>} />
                </Route>
                <Route path="tax-compliance" element={<TaxComplianceLayout />}>
                  <Route index element={<Navigate to="/finance/tax-compliance/cit" replace />} />
                  <Route path="cit"      element={<G k="finance.tax_compliance.cit"><CitAccrualPage /></G>} />
                  <Route path="vat"      element={<G k="finance.tax_compliance.vat"><VatReturnsPage /></G>} />
                  <Route path="wht"      element={<G k="finance.tax_compliance.wht"><WhtReturnsPage /></G>} />
                  <Route path="paye"     element={<G k="finance.tax_compliance.paye"><PayrollTaxReturnsPage /></G>} />
                </Route>
              </Route>
              {/* Back-compat: Payment Report moved from Finance to Trading */}
              <Route path="/finance/payments/*" element={<Navigate to="/payments" replace />} />
              {/* System — parent layout with nested sub-nav */}
              <Route path="/system" element={<SystemLayout />}>
                <Route index element={<Navigate to="/system/audit" replace />} />
                <Route path="audit"         element={<G k="system.audit"><AuditPage /></G>} />
                <Route path="messages"      element={<G k="system.messages"><PalstroMessagesPage /></G>} />
                <Route path="notifications" element={<G k="system.notifications"><NotificationsPage /></G>} />
              </Route>
              {/* Back-compat redirects for old hub URLs */}
              <Route path="/accounting-hub" element={<Navigate to="/finance/accounting" replace />} />
              <Route path="/reporting-hub"  element={<Navigate to="/finance/reporting"  replace />} />
              {/* Redirects from old routes to hub pages */}
              <Route path="/purchases" element={<Navigate to="/buy" replace />} />
              {/* Sales sub-routes keep working, bare /sales redirects to /sell */}
              <Route path="/sales/new" element={<G k="sell.invoice_new" l="edit"><NewSalePage /></G>} />
              <Route path="/sales/:id/edit" element={<G k="sell.invoice_new" l="edit"><NewSalePage /></G>} />
              <Route path="/sales/:id" element={<G k="sell.invoice_detail"><SaleDetailPage /></G>} />
              <Route path="/sales" element={<Navigate to="/sell" replace />} />
              <Route path="/stock/*"       element={<G k="inventory.stock"><StockPage /></G>} />
              <Route path="/locations/*"   element={<G k="inventory.locations"><LocationsPage /></G>} />
              <Route path="/purchases/new" element={<G k="buy.purchase_new" l="edit"><NewPurchasePage /></G>} />
              <Route path="/purchases/new-pos" element={<G k="buy.purchase_new" l="edit"><NewPurchasePagePOS /></G>} />
              <Route path="/purchases/:id/edit" element={<G k="buy.purchase_new" l="edit"><NewPurchasePage /></G>} />
              <Route path="/purchases/:id" element={<G k="buy.purchase_detail"><PurchaseDetailPage /></G>} />
              <Route path="/payroll" element={<PayrollLayout />}>
                <Route index element={<Navigate to="/payroll/staff" replace />} />
                <Route path="staff"             element={<G k="payroll.staff"><PayrollStaffPage /></G>} />
                <Route path="staff/:staffId"    element={<G k="payroll.staff_detail"><StaffDetailPage /></G>} />
                <Route path="run"      element={<G k="payroll.run" l="edit"><RunPayrollPage /></G>} />
                <Route path="payslips" element={<G k="payroll.payslips"><PayslipsPage /></G>} />
                <Route path="salary-payments" element={<G k="payroll.payslips"><SalaryPaymentsPage /></G>} />
                <Route path="settings" element={<Outlet />}>
                  <Route index element={<PayrollSettingsHubPage />} />
                  <Route path="bulk-setup" element={<G k="payroll.settings_bulk" l="edit"><BulkSetupPage /></G>} />
                  <Route path="tax"       element={<G k="payroll.settings_tax"><PayrollTaxSettingsPage /></G>} />
                  <Route path="grades"    element={<G k="payroll.settings_grades"><SalaryGradesPage /></G>} />
                  {/* Titles & pay were merged into Job Grades & Structure — keep old links working. */}
                  <Route path="salary"    element={<Navigate to="/payroll/settings/grades" replace />} />
                  <Route path="jobs"      element={<Navigate to="/payroll/settings/grades" replace />} />
                  <Route path="schedules" element={<G k="payroll.settings_schedules"><WorkingSchedulesPage /></G>} />
                </Route>
              </Route>
              <Route path="/accounting" element={<G k="accounting.hub"><AccountingPage /></G>} />
              <Route path="/accounting/journal" element={<G k="accounting.journal"><JournalPage /></G>} />
              <Route path="/accounting/chart-of-accounts" element={<G k="accounting.chart_of_accounts"><ChartOfAccountsPage /></G>} />
              <Route path="/accounting/trial-balance" element={<G k="accounting.trial_balance"><ReportGate code="trial_balance"><TrialBalancePage /></ReportGate></G>} />
              <Route path="/accounting/manual-journal" element={<G k="accounting.manual_journal" l="edit"><ManualJournalPage /></G>} />
              <Route path="/accounting/ledger" element={<G k="accounting.ledger"><LedgerPage /></G>} />
              <Route path="/accounting/opening-balances" element={<G k="accounting.opening_balances" l="edit"><OpeningBalancesPage /></G>} />
              <Route path="/accounting/subsidiary/:type/:contactId" element={<G k="accounting.subsidiary_ledger"><SubsidiaryLedgerPage /></G>} />
              <Route path="/accounting/backfill" element={<G k="accounting.backfill" l="edit"><BackfillPage /></G>} />
              <Route path="/accounting/assets" element={<G k="accounting.assets"><AssetsPage /></G>} />
              <Route path="/accounting/tax-remittance" element={<G k="accounting.tax_remittance"><TaxRemittancePage /></G>} />
              <Route path="/accounting/bank-reconciliation" element={<G k="accounting.bank_reconciliation" l="edit"><BankReconcilePage /></G>} />
              <Route path="/accounting/loans" element={<G k="accounting.loans"><LoansPage /></G>} />
              <Route path="/accounting/schedules" element={<G k="accounting.schedules"><SchedulesPage /></G>} />
              <Route path="/accounting/financial-years" element={<G k="accounting.financial_years"><FinancialYearsPage /></G>} />
              <Route path="/accounting/budget" element={<G k="accounting.budget_setup"><BudgetSetupPage /></G>} />
              <Route path="/reports/aged-debtors" element={<G k="reporting.aged_debtors"><ReportGate code="ar_aging"><AgedDebtorsPage /></ReportGate></G>} />
              <Route path="/reports/aged-creditors" element={<G k="reporting.aged_creditors"><ReportGate code="ap_aging"><AgedCreditorsPage /></ReportGate></G>} />
              <Route path="/reports/budget-vs-actual" element={<G k="reporting.budget_vs_actual"><BudgetVsActualPage /></G>} />
              <Route path="/reports/bad-debt" element={<G k="reporting.bad_debt"><BadDebtReportPage /></G>} />
              <Route path="/reports/financial-statements" element={<G k="reporting.financial_statements"><ReportGate code="draft_financials"><FinancialStatementsPage /></ReportGate></G>} />
              <Route path="/reports/sales-analysis" element={<G k="reporting.sales_analysis"><ReportGate code="sales_day_book"><SalesAnalysisPage /></ReportGate></G>} />
              <Route path="/reports/inventory-valuation" element={<G k="reporting.inventory_valuation"><ReportGate code="stock_valuation"><InventoryValuationPage /></ReportGate></G>} />
              <Route path="/reports/operations" element={<G k="reporting.operations"><OperationsReportPage /></G>} />
              <Route path="/reports/product-sales" element={<G k="reporting.sales_analysis"><ProductSalesReportPage /></G>} />
              <Route path="/reports/sales-payments" element={<G k="trading.sales_payments"><SalesPaymentReportPage /></G>} />
              <Route path="/reports/purchase-payments" element={<G k="trading.purchase_payments"><PurchasePaymentReportPage /></G>} />
              <Route path="/reports/product-purchases" element={<G k="trading.product_purchases"><ProductPurchaseReportPage /></G>} />
              <Route path="/reports/product-movement" element={<G k="inventory.product_movement"><ProductMovementReportPage /></G>} />
              {/* Non-destructive sidebar reorg demo — real routes, live nav untouched. */}
              <Route path="/nav-preview" element={<NavPreviewPage />} />
              <Route path="/reports/*"     element={<G k="reporting.hub"><ReportsPage /></G>} />
              <Route path="/sales/returns/new" element={<G k="sell.return_new" l="edit"><NewSalesReturnPage /></G>} />
              <Route path="/sales/returns/:id" element={<G k="sell.return_detail"><SaleReturnDetailPage /></G>} />
              <Route path="/sales/returns" element={<Navigate to="/sell/returns" replace />} />
              <Route path="/purchases/returns" element={<Navigate to="/buy/returns" replace />} />
              <Route path="/inventory/low-stock" element={<G k="inventory.low_stock"><LowStockPage /></G>} />
              <Route path="/inventory/stock-take" element={<G k="inventory.stock_take" l="edit"><StockTakePage /></G>} />
              <Route path="/inventory/transfers/new" element={<G k="inventory.transfers" l="edit"><NewTransferPage /></G>} />
              <Route path="/inventory/transfers/:id" element={<G k="inventory.transfers"><TransferDetailPage /></G>} />
              <Route path="/inventory/transfers" element={<G k="inventory.transfers"><StockTransfersPage /></G>} />
              <Route path="/accounting/interbank-transfer" element={<G k="accounting.interbank" l="edit"><InterbankTransferPage /></G>} />
              <Route path="/accounting/owner-drawings" element={<G k="accounting.owner_drawings" l="edit"><OwnerDrawingsPage /></G>} />
              <Route path="/accounting/ratios" element={<G k="accounting.ratios"><FinancialRatiosPage /></G>} />
              <Route path="/customers/credit-limits" element={<G k="contacts.credit_limits"><CreditLimitsPage /></G>} />
              <Route path="/budget/*"      element={<G k="accounting.budget_setup"><BudgetPage /></G>} />
              {/* Back-compat redirects — System pages now live under /system/* */}
              <Route path="/notifications"     element={<Navigate to="/system/notifications" replace />} />
              <Route path="/palstro-messages"  element={<Navigate to="/system/messages"      replace />} />
              <Route path="/audit"             element={<Navigate to="/system/audit"         replace />} />
              <Route path="/barcode"       element={<G k="inventory.barcode"><BarcodePage /></G>} />
              <Route path="/subscription"  element={<G k="system.subscription"><SubscriptionPage /></G>} />
              {/* Online-store add-on: details reachable by owner/admin/manager (controls
                  owner-only in-page); products curation owner/admin (gated in-page). No
                  plan-page guard so managers can view the countdown to nudge the owner. */}
              <Route path="/online-store"          element={<OnlineStoreDetailsPage />} />
              <Route path="/online-store/products" element={<OnlineProductsPage />} />
              <Route path="/support"       element={<G k="system.support"><SupportPage /></G>} />
              {/* Help Guide — role-aware how-to content. Ungated so every logged-in
                  user (any role/plan) can always reach it; the page filters its own
                  content to the user's role. */}
              <Route path="/help"          element={<HelpPage />} />

              {/* Logistics module — gated by plan */}
              <Route element={<ModuleGate code="logistics" />}>
                <Route path="/logistics"                      element={<G k="logistics.dashboard"><LogisticsDashboardPage /></G>} />
                <Route path="/logistics/shipments"            element={<G k="logistics.shipments"><ShipmentsPage /></G>} />
                <Route path="/logistics/shipments/new"        element={<G k="logistics.shipments" l="edit"><NewShipmentPage /></G>} />
                <Route path="/logistics/shipments/:id"        element={<G k="logistics.shipments"><ShipmentDetailPage /></G>} />
                <Route path="/logistics/fleet"                element={<G k="logistics.fleet"><FleetPage /></G>} />
                <Route path="/logistics/hubs"                 element={<G k="logistics.hubs"><HubsRoutesPage /></G>} />
                <Route path="/logistics/tracking"             element={<G k="logistics.tracking"><LiveTrackingPage /></G>} />
                <Route path="/logistics/tariffs"              element={<G k="logistics.tariffs"><TariffPage /></G>} />
                <Route path="/logistics/customers"            element={<G k="logistics.customers"><LogisticsCustomersPage /></G>} />
                <Route path="/logistics/manifests"            element={<G k="logistics.manifests"><ManifestsPage /></G>} />
                <Route path="/logistics/manifests/:id"        element={<G k="logistics.manifests"><ManifestDetailPage /></G>} />
                <Route path="/logistics/invoices"             element={<G k="logistics.invoices"><InvoicesPage /></G>} />
                <Route path="/logistics/settlements"          element={<G k="logistics.settlements"><SettlementsPage /></G>} />
              </Route>

              {/* Business Settings — nested (Phase 1 reorg) */}
              <Route path="/business-settings" element={<BusinessSettingsLayout />}>
                <Route index element={<Navigate to="/business-settings/profile" replace />} />
                <Route path="profile"           element={<G k="business_settings.profile"><BusinessProfilePage /></G>} />
                <Route path="commerce"          element={<G k="business_settings.commerce" l="edit"><CommerceSettingsPage /></G>} />
                <Route path="invoice-numbering" element={<G k="business_settings.invoice_numbering"><InvoiceNumberingPage /></G>} />
                <Route path="printer-settings"  element={<G k="business_settings.printer"><PrinterSettingsPage /></G>} />
                <Route path="locations"         element={<G k="business_settings.locations"><LocationsPage /></G>} />
                <Route path="users"             element={<G k="business_settings.users"><UsersPage /></G>} />
                {/* Phase 3 — new Roles & Permissions UI */}
                <Route path="roles"             element={<G k="business_settings.roles"><RolesListPage /></G>} />
                <Route path="roles/:roleId"     element={<G k="business_settings.role_detail"><RoleDetailPage /></G>} />
                {/* Legacy roles routes kept reachable for back-compat */}
                <Route path="roles-legacy"      element={<G k="business_settings.roles"><RolesPage /></G>} />
                <Route path="roles-legacy/new"  element={<G k="business_settings.roles" l="edit"><CreateRolePage /></G>} />
                <Route path="roles-legacy/:id"  element={<G k="business_settings.roles" l="edit"><RoleEditPage /></G>} />
                <Route path="finance" element={<FinanceSettingsLayout />}>
                  <Route index element={<Navigate to="/business-settings/finance/chart-of-accounts" replace />} />
                  <Route path="chart-of-accounts"   element={<G k="accounting.chart_of_accounts"><ChartOfAccountsPage /></G>} />
                  <Route path="financial-years"     element={<G k="accounting.financial_years"><FinancialYearsPage /></G>} />
                  <Route path="period-locks"        element={<G k="business_settings.finance.period_locks"><PeriodLocksPage /></G>} />
                  <Route path="payment-accounts"    element={<Navigate to="/business-settings/finance/chart-of-accounts" replace />} />
                  <Route path="expense-categories"  element={<G k="business_settings.finance.expense_categories"><ExpenseCategoriesPage /></G>} />
                  <Route path="currencies"          element={<G k="business_settings.finance.currencies"><CurrenciesPage /></G>} />
                  <Route path="recurring-expenses"  element={<G k="business_settings.finance.recurring_expenses"><RecurringExpensesPage /></G>} />
                  <Route path="financial-statements" element={<G k="business_settings.finance.financial_statements"><FinancialStatementSettingsPage /></G>} />
                  <Route path="tax"                  element={<G k="business_settings.finance.tax"><BusinessTaxSettingsPage /></G>} />
                </Route>
              </Route>

              {/* Settings — nested (system-level only; other settings moved to /business-settings) */}
              <Route path="/settings" element={<SettingsLayout />}>
                <Route index element={<Navigate to="/settings/audit" replace />} />
                <Route path="business"         element={<G k="business_settings.profile"><BusinessSettingsPage /></G>} />
                <Route path="receipt"          element={<G k="business_settings.profile"><BusinessSettingsPage /></G>} />
                <Route path="invoice-numbering" element={<G k="business_settings.invoice_numbering"><InvoiceNumberingPage /></G>} />
                <Route path="users"            element={<G k="business_settings.users"><UsersPage /></G>} />
                <Route path="roles"            element={<G k="business_settings.roles"><RolesPage /></G>} />
                <Route path="roles/new"        element={<G k="business_settings.roles" l="edit"><CreateRolePage /></G>} />
                <Route path="roles/:id"        element={<G k="business_settings.roles" l="edit"><RoleEditPage /></G>} />
                <Route path="modules"           element={<G k="business_settings.tenant_modules"><TenantModulesPage /></G>} />
                <Route path="role-permissions" element={<G k="business_settings.role_permissions_admin"><ModulePermissionsPage /></G>} />
                <Route path="custom-fields"    element={<G k="business_settings.custom_fields"><CustomFieldsPage /></G>} />
                <Route path="locations"        element={<G k="business_settings.locations"><LocationsPage /></G>} />
                <Route path="payment-accounts" element={<Navigate to="/business-settings/finance/chart-of-accounts" replace />} />
                <Route path="printer"          element={<G k="business_settings.printer"><PrinterSettingsPage /></G>} />
                <Route path="audit"            element={<Navigate to="/system/audit" replace />} />
                <Route path="recurring-expenses" element={<G k="business_settings.finance.recurring_expenses"><RecurringExpensesPage /></G>} />
                <Route path="currencies" element={<G k="business_settings.finance.currencies"><CurrenciesPage /></G>} />
                <Route path="logistics"        element={<G k="business_settings.logistics_settings"><LogisticsSettingsPage /></G>} />
                <Route path="financial-statements" element={<G k="business_settings.finance.financial_statements"><FinancialStatementSettingsPage /></G>} />
                <Route path="period-locks"     element={<G k="business_settings.finance.period_locks"><PeriodLocksPage /></G>} />
                <Route path="profile"          element={<G k="system.profile"><ProfilePage /></G>} />
                <Route path="subscription"     element={<G k="system.subscription"><SubscriptionPage /></G>} />
              </Route>
            </Route>
            </Route>
          </Route>

          {/* School module — gated by plan */}
          <Route element={<ProtectedRoute />}>
            <Route element={<OnboardingGuard />}>
            <Route element={<AppLayout />}>
              <Route element={<ModuleGate code="school" />}>
              <Route path="/school" element={<SchoolLayout />}>
                <Route index element={<G k="school.dashboard"><SchoolDashboard /></G>} />
                <Route path="students"          element={<G k="school.students"><StudentsPage /></G>} />
                <Route path="classes"           element={<G k="school.classes"><ClassesPage /></G>} />
                <Route path="subjects"          element={<G k="school.subjects"><SubjectsPage /></G>} />
                <Route path="staff"             element={<G k="school.staff"><SchoolStaffPage /></G>} />
                <Route path="fees"              element={<G k="school.fees"><FeeManagementPage /></G>} />
                <Route path="reports"           element={<G k="school.reports"><SchoolReportsPage /></G>} />
                <Route path="parents"           element={<G k="school.parents"><ParentPortalPage /></G>} />
                <Route path="promotion"         element={<G k="school.promotion" l="edit"><PromotionPage /></G>} />
                <Route path="id-cards"          element={<G k="school.id_cards"><IDCardsPage /></G>} />
                <Route path="fee-structure"     element={<G k="school.fee_structure"><FeeStructurePage /></G>} />
                <Route path="student-payments"  element={<G k="school.student_payments"><StudentPaymentsPage /></G>} />
                <Route path="pending-approvals" element={<G k="school.pending_approvals"><PendingApprovalsPage /></G>} />
                <Route path="bank-accounts"     element={<G k="school.bank_accounts"><BankAccountsPage /></G>} />
                <Route path="scores"            element={<G k="school.scores" l="edit"><ScoreEntryPage /></G>} />
                <Route path="report-cards"      element={<G k="school.report_cards"><ReportCardsPage /></G>} />
                <Route path="checklist"         element={<G k="school.dashboard"><SchoolDashboard /></G>} />
                <Route path="attendance"        element={<G k="school.attendance"><AttendancePage /></G>} />
                <Route path="outstanding-fees"  element={<G k="school.outstanding_fees"><OutstandingFeesPage /></G>} />
                <Route path="calendar"          element={<G k="school.calendar"><CalendarPage /></G>} />
                <Route path="alumni"            element={<G k="school.alumni"><AlumniPage /></G>} />
                <Route path="waitlist"          element={<G k="school.waitlist"><WaitlistPage /></G>} />
                <Route path="reports/discounts" element={<G k="school.reports"><DiscountReportPage /></G>} />
                <Route path="timetable"         element={<G k="school.timetable"><TimetablePage /></G>} />
                <Route path="behaviour"         element={<G k="school.behaviour"><BehaviourPage /></G>} />
                <Route path="transport"         element={<G k="school.transport"><TransportPage /></G>} />
                <Route path="hostels"           element={<G k="school.hostels"><HostelsPage /></G>} />
                <Route path="settings/staff-types"    element={<G k="school.staff" l="edit"><StaffTypesPage /></G>} />
                <Route path="settings/fee-components" element={<G k="school.fees" l="edit"><FeeComponentsPage /></G>} />
                <Route path="settings/exam-settings"  element={<G k="school.scores" l="edit"><ExamSettingsPage /></G>} />
                <Route path="settings/bank-accounts"  element={<G k="school.bank_accounts"><BankAccountsPage /></G>} />
              </Route>
              </Route>
            </Route>
            </Route>
          </Route>

          {/* Public tracking & driver app */}
          <Route path="/track/:trackingNumber" element={<PublicTrackingPage />} />
          <Route path="/driver/:driverCode"    element={<DriverAppPage />} />

          {/* Public shareable invoice */}
          <Route path="/i/:token" element={<PublicInvoicePage />} />

          {/* Storefront (Palstro Commerce) — path-based; subdomain hosts are
              handled by the early return in App() above. */}
          <Route path="/shop/:slug/*" element={<StorefrontApp />} />

          {/* Parent portal — public, separate auth flow */}
          <Route path="/parent/login"     element={<ParentLoginPage />} />
          <Route path="/parent/dashboard" element={<ParentDashboard />} />
          <Route path="/parent"           element={<ParentDashboard />} />

          {/* Admin console */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route element={<AdminProtectedRoute />}>
            <Route element={<AdminLayout />}>
              <Route path="/admin"                element={<OverviewPage />} />
              <Route path="/admin/tenants"        element={<TenantsPage />} />
              <Route path="/admin/tenants/:id"    element={<TenantDetailPage />} />
              <Route path="/admin/subscriptions"  element={<SubscriptionsAdminPage />} />
              <Route path="/admin/revenue"        element={<RevenuePage />} />
              <Route path="/admin/settings"       element={<PlatformSettingsPage />} />
              <Route path="/admin/announcements"  element={<AnnouncementsPage />} />
              <Route path="/admin/audit"          element={<PlatformAuditPage />} />
              <Route path="/admin/expenses"       element={<PlatformExpensesPage />} />
              <Route path="/admin/features"       element={<FeatureFlagsPage />} />
              <Route path="/admin/custom-domains" element={<CustomDomainsAdminPage />} />
              <Route path="/admin/showcase"       element={<ShowcaseAdminPage />} />
            </Route>
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  )
}
